/* global grecaptcha */

import $ from 'jquery'
import * as Ladda from 'ladda'
import forOwn from 'lodash/forOwn'
import moment from 'moment'
import swal from 'sweetalert2'
import { Datepicker } from 'vanillajs-datepicker'
import { setupPhoneInputs, teardownPhoneInputs } from '../../../shared/widget/phone'
import { dispatcher } from '../../dispatcher'
import { FORM_RENDERED_EVENT } from './events'

const toast = swal.mixin({
  toast: true,
  position: 'bottom-left',
  showConfirmButton: false,
  timer: 3000,
})

function upload(formEl, ladda) {
  const $form = $(formEl)
  const uploaders = $form.data('uploaders')

  const getUploadState = () => {
    const state = {
      hasUploadErrors: false,
      hasUploadsInProgress: false,
    }

    if (uploaders && uploaders.length) {
      for (let i = 0; i < uploaders.length; i += 1) {
        const uppyState = uploaders[i].getState()
        // eslint-disable-next-line no-loop-func
        forOwn(uppyState.files, (file) => {
          if ('error' in file) {
            state.hasUploadErrors = true
            return false
          }
          if (!('error' in file) && !file.progress.uploadComplete) {
            state.hasUploadsInProgress = true
            return false
          }
          return true
        })
      }
    }

    return state
  }

  if (uploaders && uploaders.length) {
    for (let i = 0; i < uploaders.length; i += 1) {
      const uppy = uploaders[i]
      if (uppy.getFiles().length) {
        uppy.upload()
      }
    }

    const state = getUploadState()
    if (state.hasUploadErrors) {
      swal('Error', 'An upload failed. Please retry or remove from the list and try sending again.', 'error').then()
      if (ladda) {
        ladda.stop()
      }
    }
  }

  return getUploadState
}

function teardownDatepickers(root) {
  root.querySelectorAll('.hidden-date, .with-datepicker').forEach((el) => {
    el.datepicker?.destroy()
  })
}

function setupDatepickers(root) {
  const $root = $(root)

  $root.find('.hidden-date, .with-datepicker').each((key, dateInput) => {
    const $dateInput = $(dateInput)
    const $dateInputs = $dateInput.closest('.date-inputs')

    // No need for datepicker if the fields are <select> elements
    if ($dateInputs.find('select').length === 3) {
      return
    }

    const $monthInput = $dateInputs.find('.date-month')
    const $dayInput = $dateInputs.find('.date-day')
    const $yearInput = $dateInputs.find('.date-year')

    const options = {
      format: 'yyyy-mm-dd',
      autoclose: true,
      startDate: -Infinity,
      endDate: Infinity,
      daysOfWeekDisabled: [],
      zIndexOffset: 999999,
    }

    if ($dateInput.data('prevent-past')) {
      const minDate = moment()
        .set({
          hour: 0,
          minute: 0,
          second: 0,
          millisecond: 0,
        })
        .toDate()
      options.startDate = minDate
      options.minDate = minDate
    }

    if ($dateInput.data('prevent-future')) {
      const maxDate = moment()
        .set({
          hour: 0,
          minute: 0,
          second: 0,
          millisecond: 0,
        })
        .toDate()
      options.endDate = maxDate
      options.maxDate = maxDate
    }

    const limitStart = $dateInput.data('limit-start')
    if (limitStart) {
      options.startDate = moment(limitStart, 'YYYY-MM-DD').toDate()
      options.minDate = moment(limitStart, 'YYYY-MM-DD').toDate()
    }

    const limitEnd = $dateInput.data('limit-end')
    if (limitEnd) {
      options.endDate = moment(limitEnd, 'YYYY-MM-DD').toDate()
      options.maxDate = moment(limitEnd, 'YYYY-MM-DD').toDate()
    }

    const daysOfWeekDisabled = []
    if ($dateInput.data('prevent-monday')) {
      daysOfWeekDisabled.push(1)
    }
    if ($dateInput.data('prevent-tuesday')) {
      daysOfWeekDisabled.push(2)
    }
    if ($dateInput.data('prevent-wednesday')) {
      daysOfWeekDisabled.push(3)
    }
    if ($dateInput.data('prevent-thursday')) {
      daysOfWeekDisabled.push(4)
    }
    if ($dateInput.data('prevent-friday')) {
      daysOfWeekDisabled.push(5)
    }
    if ($dateInput.data('prevent-saturday')) {
      daysOfWeekDisabled.push(6)
    }
    if ($dateInput.data('prevent-sunday')) {
      daysOfWeekDisabled.push(7)
    }
    options.daysOfWeekDisabled = daysOfWeekDisabled

    const onChangeDate = (ev) => {
      const date = moment(ev.detail.date)
      $monthInput.val(date.format('MM'))
      $dayInput.val(date.format('DD'))
      $yearInput.val(date.format('YYYY'))
    }

    const datepicker = new Datepicker(dateInput, options)
    dateInput.addEventListener('changeDate', onChangeDate)

    const onFocus = () => {
      datepicker.show()
    }

    $monthInput.on('focus', onFocus)
    $dayInput.on('focus', onFocus)
    $yearInput.on('focus', onFocus)
  })
}

let saveForLaterLadda = null

async function saveForLater(formEl) {
  const continuationUrl = formEl.getAttribute('data-continuation-url')
  if (!continuationUrl) {
    return Promise.resolve()
  }
  return new Promise((resolve, reject) => {
    if (saveForLaterLadda) {
      saveForLaterLadda.start()
    }
    $.ajax({
      type: 'POST',
      url: continuationUrl,
      data: $(formEl.elements).not('[name*="_token"],[name*="[blue]"],[name*="g-recaptcha-response"]').serialize(),
      global: false,
    })
      .done(() => {
        toast({
          type: 'success',
          title: 'Saved',
        })
        resolve()
      })
      .fail(() => {
        reject()
      })
      .always(() => {
        if (saveForLaterLadda) {
          saveForLaterLadda.stop()
        }
      })
  })
}

function setupSaveForLater(formEl) {
  const saveForLaterButton = formEl.querySelector('.js-save-for-later')
  if (saveForLaterButton) {
    saveForLaterLadda = Ladda.create(saveForLaterButton)
    saveForLaterButton.addEventListener('click', () => {
      const getUploadState = upload(formEl, saveForLaterLadda)
      let state = getUploadState()
      if (state.hasUploadsInProgress) {
        let postTimer = null
        postTimer = setInterval(() => {
          state = getUploadState()
          if (state.hasUploadsInProgress) {
            return
          }
          clearInterval(postTimer)
          saveForLater(formEl)
        }, 1000)
      } else {
        saveForLater(formEl)
      }
    })
  }
}

function teardownSaveForLater() {
  if (saveForLaterLadda) {
    saveForLaterLadda.remove()
  }
}

function setupForm(formEl) {
  // eslint-disable-next-line no-param-reassign,prefer-destructuring
  if (typeof formEl === 'object' && formEl.jquery) formEl = formEl[0]
  setupDatepickers(formEl)
  setupPhoneInputs(formEl)
  setupSaveForLater(formEl)
  dispatcher.dispatch(FORM_RENDERED_EVENT, formEl)
}

function teardownForm(formEl) {
  teardownDatepickers(formEl)
  teardownPhoneInputs()
  teardownSaveForLater(formEl)
}

function setupForms(container) {
  if (!container) {
    return
  }

  // Initialize existing elements:

  const existingEls = container.querySelectorAll('form.js-dynamic-form')
  if (existingEls.length) {
    existingEls.forEach((el) => setupForm(el))
  }

  // Observe new elements:

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) {
          return
        }

        const els = node.querySelectorAll('form.js-dynamic-form')
        if (!els.length) {
          return
        }

        els.forEach((el) => setupForm(el))
      })
    })
  })

  observer.observe(container, {
    childList: true,
    subtree: true,
    attributes: false,
    characterData: false,
  })
}

setupForms(document)

$(document).on('submit', 'form.js-dynamic-form', (ev) => {
  const $form = $(ev.currentTarget)
  if ($form.hasClass('without-ajax')) {
    return true
  }

  ev.preventDefault()

  const l = Ladda.create($form.find('button[type=submit]')[0])
  l.start()

  //
  // Uploaders
  //

  const getUploadState = upload(ev.currentTarget, l)

  //
  // POST
  //

  const doPost = () => {
    $.ajax({
      type: 'POST',
      url: ev.currentTarget.getAttribute('action'),
      data: $form.serialize(),
      dataType: 'json',
      success: (resp) => {
        if ('html' in resp) {
          teardownForm($form[0])
          const $newForm = $($.parseHTML(resp.html))
          $form.replaceWith($newForm)
          $newForm.find('.form-group.with-error').first().find('input:first, select:first, textarea:first').focus()
          setupForm($newForm.filter('form')[0])
          $newForm.trigger('submitHandled')
        } else if ('redirect' in resp) {
          window.location = resp.redirect
        }
      },
    }).always(() => {
      l.remove()
    })
  }

  let state = getUploadState()
  if (state.hasUploadsInProgress) {
    let postTimer = null
    postTimer = setInterval(() => {
      state = getUploadState()
      if (state.hasUploadsInProgress) {
        return
      }
      clearInterval(postTimer)
      doPost()
    }, 1000)
  } else {
    doPost()
  }

  return true
})
